import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import inpiService from '../services/inpiService';
import CompanyOverviewSection from '../components/adherent/CompanyOverviewSection';
import ContactSection from '../components/adherent/ContactSection';
import AdminSection from '../components/adherent/AdminSection';
import EstablishmentSection from '../components/adherent/EstablishmentSection';
import GovernanceSection from '../components/adherent/GovernanceSection';
import InseeEstablishmentsCard from '../components/insee/InseeEstablishmentsCard';
import NotesButton from '../components/notes/NotesButton';
import EstablishmentStatusBanner from '../components/adherent/EstablishmentStatusBanner';
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';
import { LegalActsData, AnnualAccountsData } from '../types/inpi';

// Define types for the INPI data
interface CompanyData {
  siren: string;
  // Add other properties as needed
  formality?: any;
  capital?: any;
  forme_juridique?: string;
  date_immatriculation?: string;
  date_radiation?: string;
  dirigeants?: any[];
}

interface Adherent {
  id: number;
  siret: string;
  contactName: string | null;
  contactPhone: string | null;
  contactEmail: string | null;
  description: string | null;
  createdAt: string;
  importFileId: number;
  importFile: {
    id: number;
    originalFilename: string;
    createdAt: string;
    status: string;
  };
  bigData?: {
    uniteLegale: any;
    etablissement: any;
  };
}

const AdherentDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [adherent, setAdherent] = useState<Adherent | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState<boolean>(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<boolean>(false);
  
  // INPI data states
  const [companyData, setCompanyData] = useState<CompanyData | null>(null);
  const [companyLoading, setCompanyLoading] = useState<boolean>(false);
  const [companyError, setCompanyError] = useState<string | null>(null);
  
  // Annual accounts state
  const [annualAccounts, setAnnualAccounts] = useState<AnnualAccountsData | null>(null);
  const [annualAccountsLoading, setAnnualAccountsLoading] = useState<boolean>(false);
  const [annualAccountsError, setAnnualAccountsError] = useState<string | null>(null);
  const [annualAccountsPage, setAnnualAccountsPage] = useState<number>(1);

  // Legal acts state
  const [legalActs, setLegalActs] = useState<LegalActsData | null>(null);
  const [legalActsLoading, setLegalActsLoading] = useState<boolean>(false);
  const [legalActsError, setLegalActsError] = useState<string | null>(null);
  const [legalActsPage, setLegalActsPage] = useState<number>(1);

  // Fetch adherent data
  useEffect(() => {
    const fetchAdherentDetails = async () => {
      setIsLoading(true);
      setError(null);
  
      try {
        const response = await axios.get(
          `${import.meta.env.VITE_API_BASE_URL}/adherents/${id}`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem('token')}`,
            },
          }
        );
  
        setAdherent(response.data);
      } catch (error: any) {
        console.error('Error fetching adherent details:', error);
        setError(
          error.response?.data?.message ||
            'Une erreur est survenue lors de la récupération des détails de l\'adhérent'
        );
      } finally {
        setIsLoading(false);
      }
    };

    fetchAdherentDetails();
  }, [id]);

  // Fetch INPI data when adherent is loaded
  useEffect(() => {
    const fetchInpiData = async () => {
      if (!adherent?.siret) return;
      
      const siren = adherent.siret.substring(0, 9);
      
      setCompanyLoading(true);
      setCompanyError(null);
      
      try {
        const data = await inpiService.getCompanyBySiren(siren);
        setCompanyData(data);
      } catch (error: any) {
        console.error('Error fetching company data:', error);
        setCompanyError('Données INPI temporairement indisponibles');
      } finally {
        setCompanyLoading(false);
      }
    };

    fetchInpiData();
  }, [adherent]);

  // Fetch annual accounts
  useEffect(() => {
    const fetchAnnualAccounts = async () => {
      if (!adherent?.siret) return;
      
      const siren = adherent.siret.substring(0, 9);
      
      setAnnualAccountsLoading(true);
      setAnnualAccountsError(null);
      
      try {
        const data = await inpiService.getAnnualAccounts(siren, {
          page: annualAccountsPage,
          per_page: 10
        });
        setAnnualAccounts(data);
      } catch (error: any) {
        console.error('Error fetching annual accounts:', error);
        setAnnualAccountsError('Données temporairement indisponibles');
      } finally {
        setAnnualAccountsLoading(false);
      }
    };

    fetchAnnualAccounts();
  }, [adherent, annualAccountsPage]);

  // Fetch legal acts
  useEffect(() => {
    const fetchLegalActs = async () => {
      if (!adherent?.siret) return;
      
      const siren = adherent.siret.substring(0, 9);
      
      setLegalActsLoading(true);
      setLegalActsError(null);
      
      try {
        const data = await inpiService.getLegalActs(siren, {
          page: legalActsPage,
          per_page: 10
        });
        setLegalActs(data);
      } catch (error: any) {
        console.error('Error fetching legal acts:', error);
        setLegalActsError('Données temporairement indisponibles');
      } finally {
        setLegalActsLoading(false);
      }
    };

    fetchLegalActs();
  }, [adherent, legalActsPage]);

  const handleDelete = async () => {
    setIsDeleting(true);
    setError(null);

    try {
      await axios.delete(
        `${import.meta.env.VITE_API_BASE_URL}/adherents/${id}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          },
        }
      );

      navigate('/adherents', { 
        state: { 
          notification: {
            type: 'success',
            message: 'Adhérent supprimé avec succès'
          }
        }
      });
    } catch (error: any) {
      console.error('Error deleting adherent:', error);
      setError(
        error.response?.data?.message ||
          'Une erreur est survenue lors de la suppression de l\'adhérent'
      );
      setIsDeleting(false);
      setShowDeleteConfirm(false);
    }
  };

  // Handle page changes
  const handleAnnualAccountsPageChange = (page: number) => {
    setAnnualAccountsPage(page);
  };

  const handleLegalActsPageChange = (page: number) => {
    setLegalActsPage(page);
  };

  // Global loading state
  if (isLoading) {
    return (
      <div className="p-6">
        <div className="text-center py-8">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Chargement des détails de l'adhérent...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="p-6">
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative">
          <strong className="font-bold">Erreur: </strong>
          <span className="block sm:inline">{error}</span>
        </div>
        <div className="mt-4">
          <Link
            to="/adherents"
            className="text-blue-600 hover:text-blue-800"
          >
            &larr; Retour à la liste des adhérents
          </Link>
        </div>
      </div>
    );
  }

  // No data state
  if (!adherent) {
    return (
      <div className="p-6">
        <div className="bg-yellow-100 border border-yellow-400 text-yellow-700 px-4 py-3 rounded relative">
          <strong className="font-bold">Attention: </strong>
          <span className="block sm:inline">Adhérent non trouvé</span>
        </div>
        <div className="mt-4">
          <Link
            to="/adherents"
            className="text-blue-600 hover:text-blue-800"
          >
            &larr; Retour à la liste des adhérents
          </Link>
        </div>
      </div>
    );
  }

  // Get company name for display
  const getCompanyName = () => {
    return adherent.bigData?.uniteLegale?.denominationUniteLegale || 
      (adherent.bigData?.uniteLegale?.nomUniteLegale && adherent.bigData?.uniteLegale?.prenom1UniteLegale ? 
        `${adherent.bigData.uniteLegale.prenom1UniteLegale} ${adherent.bigData.uniteLegale.nomUniteLegale}` : 
        adherent.bigData?.etablissement?.denominationUsuelleEtablissement || 
        adherent.description || 'Adhérent');
  };

  // Get SIREN from SIRET
  const getSiren = () => {
    return adherent.siret.substring(0, 9);
  };

  // Skeleton for loading sections
  const renderSectionSkeleton = () => (
    <div className="bg-white shadow rounded-lg overflow-hidden">
      <div className="p-6">
        <Skeleton height={30} width="40%" className="mb-4" />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <Skeleton count={5} />
          </div>
          <div>
            <Skeleton count={5} />
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="p-6 space-y-6">
      {/* Back link */}
      <div className='-mt-4'>
        <Link
          to="/adherents"
          className="text-blue-600 hover:text-blue-800 flex items-center"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
          </svg>
          Retour à la liste des adhérents
        </Link>
      </div>
      
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">
            {getCompanyName()}
          </h1>
          <p className="mt-1 text-sm text-gray-500">
            SIRET: <span className="font-medium">{adherent.siret}</span>
          </p>
        </div>
        <div className="flex space-x-3">
          <NotesButton
            siret={adherent.siret}
            companyName={getCompanyName()}
            variant="button"
            size="md"
          />
          <Link
            to={`/adherents/${id}/edit`}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors"
          >
            Modifier
          </Link>
          <button
            onClick={() => setShowDeleteConfirm(true)}
            className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 transition-colors"
          >
            Supprimer
          </button>
        </div>
      </div>

      {/* Status Banner */}
      {!isLoading && adherent?.bigData?.etablissement && (
        <EstablishmentStatusBanner 
          etatAdministratifEtablissement={adherent.bigData.etablissement.etatAdministratifEtablissement} 
        />
      )}

      {/* 1. Établissement adhérent and Contact Section */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Établissement adhérent */}
        <div className="md:col-span-2">
          {isLoading ? (
            renderSectionSkeleton()
          ) : adherent.bigData && (
            <EstablishmentSection
              siren={getSiren()}
              siret={adherent.siret}
              etablissement={adherent.bigData?.etablissement || null}
              isLoading={false}
              description={adherent.description}
              companyName={getCompanyName()}
            />
          )}
        </div>
        
        {/* Contact Information */}
        <div className="md:col-span-1">
          {isLoading ? (
            renderSectionSkeleton()
          ) : (
            <ContactSection
              contactName={adherent.contactName}
              contactPhone={adherent.contactPhone}
              contactEmail={adherent.contactEmail}
              description={adherent.description}
              isLoading={false}
            />
          )}
        </div>
      </div>

      {/* 2. Company overview section with directors */}
      {companyLoading ? (
        renderSectionSkeleton()
      ) : (
        <CompanyOverviewSection 
          siren={getSiren()}
          uniteLegale={adherent.bigData?.uniteLegale || null}
          companyData={companyData}
          companyDataLoading={companyLoading}
          companyError={companyError}
        />
      )}
      
      {/* 3. All Establishments section */}
      {isLoading ? (
        renderSectionSkeleton()
      ) : adherent.bigData && (
        <div className="bg-white shadow rounded-lg overflow-hidden">
          <div className="px-4 py-5 sm:px-6 bg-gray-50 border-b border-gray-200">
            <h3 className="text-lg font-medium leading-6 text-gray-900">
              Tous les établissements de l'unité légale
            </h3>
            <p className="mt-1 max-w-2xl text-sm text-gray-500">
              Liste complète des établissements
            </p>
          </div>
          <div className="p-6">
            <InseeEstablishmentsCard siren={getSiren()} />
          </div>
        </div>
      )}

      {/* 4. Legal Documents Section (previously Governance Section) */}
      {!companyData ? (
        companyLoading ? renderSectionSkeleton() : null
      ) : (
        <GovernanceSection
          companyData={companyData}
          companyLoading={companyLoading}
          companyError={companyError}
          annualAccounts={annualAccounts}
          annualAccountsLoading={annualAccountsLoading}
          annualAccountsError={annualAccountsError}
          legalActs={legalActs}
          legalActsLoading={legalActsLoading}
          legalActsError={legalActsError}
          onAnnualAccountsPageChange={handleAnnualAccountsPageChange}
          onLegalActsPageChange={handleLegalActsPageChange}
        />
      )}
      
      {/* 5. Administrative Information (import details) */}
      {isLoading ? (
        renderSectionSkeleton()
      ) : (
        <AdminSection
          createdAt={adherent.createdAt}
          importFile={adherent.importFile}
          isLoading={false}
        />
      )}

      {/* Delete confirmation modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Confirmer la suppression</h3>
            <p className="text-sm text-gray-500 mb-4">
              Êtes-vous sûr de vouloir supprimer cet adhérent ? Cette action est irréversible.
            </p>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setShowDeleteConfirm(false)}
                className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
                disabled={isDeleting}
              >
                Annuler
              </button>
              <button
                onClick={handleDelete}
                className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
                disabled={isDeleting}
              >
                {isDeleting ? 'Suppression...' : 'Supprimer'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdherentDetail; 